import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';

// Game constants that adapt to screen size
const getGameDimensions = () => {
  const isMobile = window.innerWidth < 768;
  return {
    GAME_HEIGHT: isMobile ? 400 : 600,
    GAME_WIDTH: isMobile ? Math.min(window.innerWidth - 30, 400) : 800,
    ARROW_WIDTH: isMobile ? 40 : 60,
    ARROW_HEIGHT: isMobile ? 10 : 15,
    TARGET_SIZE: isMobile ? 40 : 50
  };
};

// Level configurations
const LEVELS = [
  {
    id: 1,
    name: "The Reflection Test",
    description: "Shoot the fish by aiming at its reflection in the water",
    background: "from-blue-300 to-blue-500",
    target: { type: "fish", movement: "spinning" },
    environment: "water",
    reward: "Focus of the Mind 🧘‍♂️"
  },
  {
    id: 2,
    name: "The Fire Circle",
    description: "Hit the glowing chakra ring that spins and changes size. Time your shot between fire sparks!",
    background: "from-orange-400 to-red-600",
    target: { type: "chakra", movement: "spinning" },
    environment: "fire",
    reward: "Gandiva Bow 🏹"
  },
  {
    id: 3,
    name: "Indra's Storm",
    description: "Shoot flying orbs during thunder flashes. Watch out for lightning!",
    background: "from-gray-500 to-gray-800",
    target: { type: "orb", movement: "flying" },
    environment: "storm",
    reward: "Bravery Under Chaos ⚡"
  },
  {
    id: 4,
    name: "Divine Duel",
    description: "Hit dodging demons that try to avoid your arrows",
    background: "from-purple-500 to-indigo-700",
    target: { type: "demon", movement: "dodging" },
    environment: "battlefield",
    reward: "Vijaya Bow ⚔️"
  },
  {
    id: 5,
    name: "The Dharma Trial",
    description: "Hit all five elemental targets (Earth, Water, Fire, Air, Space) in one round",
    background: "from-yellow-300 to-orange-500",
    target: { type: "elemental", count: 5 },
    environment: "divine",
    reward: "True Archer of Dharma 🕉️"
  }
];

const ArjunaArcheryChallenge = () => {
  // Get initial dimensions
  const initialDimensions = getGameDimensions();
  
  const [gameState, setGameState] = useState('menu'); // menu, levelSelect, playing, levelComplete, gameOver
  const [gameDimensions, setGameDimensions] = useState(initialDimensions);
  const [currentLevel, setCurrentLevel] = useState(0);
  const [arrowPosition, setArrowPosition] = useState({ x: 100, y: initialDimensions.GAME_HEIGHT / 2 });
  const [arrowVelocity, setArrowVelocity] = useState({ x: 0, y: 0 });
  const [isAiming, setIsAiming] = useState(false);
  const [aimAngle, setAimAngle] = useState(0);
  const [aimPower, setAimPower] = useState(0);
  const [targets, setTargets] = useState([]);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(localStorage.getItem('arjunaArcheryHighScore') || 0);
  const [lives, setLives] = useState(3);
  const [wind, setWind] = useState({ speed: 0, direction: 1 });
  const [unlockedBows, setUnlockedBows] = useState(['Basic Bow']);
  const [wisdomQuotes, setWisdomQuotes] = useState([]);
  const [fireSparks, setFireSparks] = useState([]); // For Level 2 fire effects
  
  const gameLoopRef = useRef();
  const targetSpawnRef = useRef();
  const windChangeRef = useRef();
  const fireSparkRef = useRef();
  const lastTimeRef = useRef();
  const audioContextRef = useRef(null);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      const dimensions = getGameDimensions();
      setGameDimensions(dimensions);
      
      // Adjust arrow position proportionally
      setArrowPosition(prev => ({
        x: (prev.x / gameDimensions.GAME_WIDTH) * dimensions.GAME_WIDTH || 100,
        y: (prev.y / gameDimensions.GAME_HEIGHT) * dimensions.GAME_HEIGHT || dimensions.GAME_HEIGHT / 2
      }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [gameDimensions]);

  // Initialize audio context
  const initAudio = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
  };

  // Play sound effects
  const playSound = (type) => {
    initAudio();
    const audioContext = audioContextRef.current;
    
    if (!audioContext) return;
    
    try {
      switch (type) {
        case 'shoot':
          // Create a shooting sound
          const shootOscillator = audioContext.createOscillator();
          const shootGain = audioContext.createGain();
          
          shootOscillator.connect(shootGain);
          shootGain.connect(audioContext.destination);
          
          shootOscillator.type = 'sine';
          shootOscillator.frequency.setValueAtTime(523.25, audioContext.currentTime); // C5
          shootOscillator.frequency.exponentialRampToValueAtTime(1046.50, audioContext.currentTime + 0.3);
          shootGain.gain.setValueAtTime(0.2, audioContext.currentTime);
          shootGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
          
          shootOscillator.start(audioContext.currentTime);
          shootOscillator.stop(audioContext.currentTime + 0.3);
          break;
          
        case 'hit':
          // Create a pleasant sound for hitting targets
          const hitOscillator = audioContext.createOscillator();
          const hitGain = audioContext.createGain();
          
          hitOscillator.connect(hitGain);
          hitGain.connect(audioContext.destination);
          
          hitOscillator.type = 'sine';
          hitOscillator.frequency.setValueAtTime(783.99, audioContext.currentTime); // G5
          hitGain.gain.setValueAtTime(0.3, audioContext.currentTime);
          hitGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
          
          hitOscillator.start(audioContext.currentTime);
          hitOscillator.stop(audioContext.currentTime + 0.5);
          break;
          
        case 'miss':
          // Create a sound for missing targets
          const missOscillator = audioContext.createOscillator();
          const missGain = audioContext.createGain();
          
          missOscillator.connect(missGain);
          missGain.connect(audioContext.destination);
          
          missOscillator.type = 'sine';
          missOscillator.frequency.setValueAtTime(220, audioContext.currentTime); // A3
          missGain.gain.setValueAtTime(0.2, audioContext.currentTime);
          missGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
          
          missOscillator.start(audioContext.currentTime);
          missOscillator.stop(audioContext.currentTime + 0.5);
          break;
          
        case 'levelComplete':
          // Create a celebratory sound
          const levelOscillator = audioContext.createOscillator();
          const levelGain = audioContext.createGain();
          
          levelOscillator.connect(levelGain);
          levelGain.connect(audioContext.destination);
          
          levelOscillator.type = 'sine';
          levelOscillator.frequency.setValueAtTime(523.25, audioContext.currentTime); // C5
          levelOscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.2); // E5
          levelOscillator.frequency.setValueAtTime(783.99, audioContext.currentTime + 0.4); // G5
          levelOscillator.frequency.setValueAtTime(1046.50, audioContext.currentTime + 0.6); // C6
          levelGain.gain.setValueAtTime(0.3, audioContext.currentTime);
          levelGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 1.0);
          
          levelOscillator.start(audioContext.currentTime);
          levelOscillator.stop(audioContext.currentTime + 1.0);
          break;
          
        default:
          break;
      }
    } catch (error) {
      console.log('Sound error:', error);
    }
  };

  // Start aiming
  const startAiming = (e) => {
    if (gameState !== 'playing') return;
    
    // Get the game container element
    const gameContainer = document.querySelector('.game-container');
    if (!gameContainer) return;
    
    const rect = gameContainer.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    // Calculate angle from bow position to mouse
    const dx = mouseX - arrowPosition.x;
    const dy = mouseY - arrowPosition.y;
    const angle = Math.atan2(dy, dx);
    
    setIsAiming(true);
    setAimAngle(angle);
  };

  // Update aim while dragging
  const updateAim = useCallback((e) => {
    if (!isAiming || gameState !== 'playing') return;
    
    // Get the game container element
    const gameContainer = document.querySelector('.game-container');
    if (!gameContainer) return;
    
    const rect = gameContainer.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    // Calculate angle and power
    const dx = mouseX - arrowPosition.x;
    const dy = mouseY - arrowPosition.y;
    const angle = Math.atan2(dy, dx);
    const power = Math.min(Math.sqrt(dx * dx + dy * dy) / 10, 20); // Cap power at 20
    
    setAimAngle(angle);
    setAimPower(power);
  }, [isAiming, arrowPosition, gameState]);

  // Shoot arrow
  const shootArrow = useCallback(() => {
    if (!isAiming || gameState !== 'playing') return;
    
    setIsAiming(false);
    playSound('shoot');
    
    // Set arrow velocity based on aim angle and power
    const velocityX = Math.cos(aimAngle) * aimPower;
    const velocityY = Math.sin(aimAngle) * aimPower;
    
    setArrowVelocity({ x: velocityX, y: velocityY });
  }, [isAiming, aimAngle, aimPower, gameState]);

  // Handle mouse events for aiming
  useEffect(() => {
    const handleMouseMove = (e) => {
      updateAim(e);
    };
    
    const handleMouseUp = () => {
      if (isAiming) {
        shootArrow();
      }
    };
    
    if (isAiming) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isAiming, updateAim, shootArrow]);

  // Game loop
  useEffect(() => {
    if (gameState !== 'playing') return;

    const updateGame = (timestamp) => {
      if (!lastTimeRef.current) lastTimeRef.current = timestamp;
      const deltaTime = timestamp - lastTimeRef.current;
      lastTimeRef.current = timestamp;

      // Update arrow position if it's been shot
      if (arrowVelocity.x !== 0 || arrowVelocity.y !== 0) {
        setArrowPosition(prev => {
          // Apply wind effect
          const windEffect = wind.speed * wind.direction * 0.1;
          
          // Apply gravity
          const newVelocityY = arrowVelocity.y + 0.2;
          
          const newX = prev.x + arrowVelocity.x + windEffect;
          const newY = prev.y + newVelocityY;
          
          // Check if arrow is out of bounds
          if (newX > gameDimensions.GAME_WIDTH || newX < 0 || newY > gameDimensions.GAME_HEIGHT || newY < 0) {
            // Arrow missed - lose a life
            setLives(prevLives => {
              const newLives = prevLives - 1;
              if (newLives <= 0) {
                setGameState('gameOver');
                if (score > highScore) {
                  setHighScore(score);
                  localStorage.setItem('arjunaArcheryHighScore', score);
                }
              }
              return newLives;
            });
            
            // Reset arrow
            setArrowPosition({ x: 100, y: gameDimensions.GAME_HEIGHT / 2 });
            setArrowVelocity({ x: 0, y: 0 });
            playSound('miss');
            return { x: 100, y: gameDimensions.GAME_HEIGHT / 2 };
          }
          
          return { x: newX, y: newY };
        });
        
        // Update velocity with wind effect
        setArrowVelocity(prev => {
          const windEffect = wind.speed * wind.direction * 0.05;
          return { 
            x: prev.x + windEffect, 
            y: prev.y + 0.2 // Gravity
          };
        });
      }

      // Move targets based on level
      setTargets(prev => {
        return prev.map(target => {
          let newX = target.x;
          let newY = target.y;
          
          switch (LEVELS[currentLevel].target.movement) {
            case 'spinning':
              // Spin in a circular motion - slower and more predictable for Level 2
              const speed = currentLevel === 1 ? 0.0005 : 0.001; // Slower for Level 2
              const angle = (target.id * 0.01 + timestamp * speed) % (Math.PI * 2);
              newX = target.centerX + Math.cos(angle) * target.radius;
              newY = target.centerY + Math.sin(angle) * target.radius;
              break;
            case 'flying':
              // Fly horizontally with occasional vertical movement
              newX = target.x + target.speed;
              if (Math.random() < 0.02) {
                newY = target.y + (Math.random() - 0.5) * 10;
              }
              break;
            case 'dodging':
              // Move in a zigzag pattern
              newX = target.x + target.speed;
              newY = target.y + Math.sin(timestamp * 0.005 + target.id) * 2;
              break;
            default:
              break;
          }
          
          // Reset position if target goes off screen
          if (newX > gameDimensions.GAME_WIDTH + 50) {
            newX = -50;
          } else if (newX < -50) {
            newX = gameDimensions.GAME_WIDTH + 50;
          }
          
          return { ...target, x: newX, y: newY };
        });
      });

      // Collision detection
      const arrowRect = {
        x: arrowPosition.x,
        y: arrowPosition.y,
        width: gameDimensions.ARROW_WIDTH,
        height: gameDimensions.ARROW_HEIGHT
      };

      for (let target of targets) {
        // Adjust target size based on type
        const targetSize = target.size || gameDimensions.TARGET_SIZE;
        const targetRect = {
          x: target.x - targetSize / 2,
          y: target.y - targetSize / 2,
          width: targetSize,
          height: targetSize
        };

        if (
          arrowRect.x < targetRect.x + targetRect.width &&
          arrowRect.x + arrowRect.width > targetRect.x &&
          arrowRect.y < targetRect.y + targetRect.height &&
          arrowRect.y + arrowRect.height > targetRect.y
        ) {
          // Hit target
          playSound('hit');
          setScore(prev => prev + 100);
          
          // Remove hit target
          setTargets(prev => prev.filter(t => t.id !== target.id));
          
          // Check if level is complete
          if (targets.length <= 1) {
            completeLevel();
          }
          
          // Reset arrow
          setArrowPosition({ x: 100, y: gameDimensions.GAME_HEIGHT / 2 });
          setArrowVelocity({ x: 0, y: 0 });
          break;
        }
      }

      gameLoopRef.current = requestAnimationFrame(updateGame);
    };

    gameLoopRef.current = requestAnimationFrame(updateGame);
    return () => {
      if (gameLoopRef.current) cancelAnimationFrame(gameLoopRef.current);
    };
  }, [gameState, arrowPosition, arrowVelocity, targets, currentLevel, wind, score, highScore, lives, gameDimensions]);

  // Spawn targets
  useEffect(() => {
    if (gameState !== 'playing') return;

    const spawnTargets = () => {
      const newTargets = [];
      const targetCount = LEVELS[currentLevel].target.count || 1;
      
      for (let i = 0; i < targetCount; i++) {
        let target;
        
        switch (LEVELS[currentLevel].target.type) {
          case 'fish':
            target = {
              id: Date.now() + i,
              x: gameDimensions.GAME_WIDTH / 2,
              y: gameDimensions.GAME_HEIGHT / 3,
              centerX: gameDimensions.GAME_WIDTH / 2,
              centerY: gameDimensions.GAME_HEIGHT / 3,
              radius: gameDimensions.GAME_WIDTH / 6,
              type: 'fish'
            };
            break;
          case 'chakra':
            target = {
              id: Date.now() + i,
              x: gameDimensions.GAME_WIDTH / 2,
              y: gameDimensions.GAME_HEIGHT / 2,
              centerX: gameDimensions.GAME_WIDTH / 2,
              centerY: gameDimensions.GAME_HEIGHT / 2,
              radius: gameDimensions.GAME_WIDTH / 8,
              size: gameDimensions.TARGET_SIZE * 1.5, // Larger target for easier hitting
              type: 'chakra'
            };
            break;
          case 'orb':
            target = {
              id: Date.now() + i,
              x: -50,
              y: Math.random() * (gameDimensions.GAME_HEIGHT - 100) + 50,
              speed: 2 + Math.random() * 3,
              type: 'orb'
            };
            break;
          case 'demon':
            target = {
              id: Date.now() + i,
              x: gameDimensions.GAME_WIDTH + 50,
              y: Math.random() * (gameDimensions.GAME_HEIGHT - 100) + 50,
              speed: -2 - Math.random() * 2,
              type: 'demon'
            };
            break;
          case 'elemental':
            target = {
              id: Date.now() + i,
              x: Math.random() * (gameDimensions.GAME_WIDTH - 100) + 50,
              y: Math.random() * (gameDimensions.GAME_HEIGHT - 100) + 50,
              element: ['earth', 'water', 'fire', 'air', 'space'][i],
              type: 'elemental'
            };
            break;
          default:
            target = {
              id: Date.now() + i,
              x: Math.random() * (gameDimensions.GAME_WIDTH - 100) + 50,
              y: Math.random() * (gameDimensions.GAME_HEIGHT - 100) + 50,
              type: 'default'
            };
        }
        
        newTargets.push(target);
      }
      
      setTargets(newTargets);
    };

    spawnTargets();

    return () => {
      if (targetSpawnRef.current) clearTimeout(targetSpawnRef.current);
    };
  }, [gameState, currentLevel, gameDimensions]);

  // Spawn fire sparks for Level 2
  useEffect(() => {
    if (gameState !== 'playing' || currentLevel !== 1) return; // Level 2 is index 1

    const spawnFireSpark = () => {
      setFireSparks(prev => [
        ...prev,
        {
          id: Date.now(),
          x: Math.random() * gameDimensions.GAME_WIDTH,
          y: Math.random() * gameDimensions.GAME_HEIGHT,
          size: Math.random() * 10 + 5
        }
      ]);

      // Remove old sparks
      setTimeout(() => {
        setFireSparks(prev => prev.slice(1));
      }, 1000);

      fireSparkRef.current = setTimeout(spawnFireSpark, 300 + Math.random() * 500);
    };

    fireSparkRef.current = setTimeout(spawnFireSpark, 1000);

    return () => {
      if (fireSparkRef.current) clearTimeout(fireSparkRef.current);
    };
  }, [gameState, currentLevel, gameDimensions]);

  // Change wind periodically
  useEffect(() => {
    if (gameState !== 'playing') return;

    const changeWind = () => {
      setWind({
        speed: Math.random() * 4,
        direction: Math.random() > 0.5 ? 1 : -1
      });
      
      windChangeRef.current = setTimeout(changeWind, 5000 + Math.random() * 5000);
    };

    windChangeRef.current = setTimeout(changeWind, 3000);

    return () => {
      if (windChangeRef.current) clearTimeout(windChangeRef.current);
    };
  }, [gameState]);

  const startGame = () => {
    setGameState('levelSelect');
  };

  const startLevel = (levelIndex) => {
    setCurrentLevel(levelIndex);
    setGameState('playing');
    setArrowPosition({ x: 100, y: gameDimensions.GAME_HEIGHT / 2 });
    setArrowVelocity({ x: 0, y: 0 });
    setTargets([]);
    setLives(3);
    setWind({ speed: 0, direction: 1 });
    lastTimeRef.current = null;
  };

  const completeLevel = () => {
    playSound('levelComplete');
    setGameState('levelComplete');
    
    // Unlock bow if applicable
    const level = LEVELS[currentLevel];
    if (level.reward.includes('Bow') || level.reward.includes('Dharma')) {
      const newBow = level.reward.split(' ')[0]; // Extract bow name
      if (!unlockedBows.includes(newBow)) {
        setUnlockedBows(prev => [...prev, newBow]);
      }
    }
    
    // Add wisdom quote
    const quotes = [
      "A true warrior conquers his mind before his enemy. – Dronacharya",
      "Your focus determines your destiny. – Krishna",
      "Patience is the companion of wisdom. – Chanakya",
      "The mind is everything; what you think you become. – Buddha",
      "Self-control is the mark of a mighty man. – Chanakya"
    ];
    
    setWisdomQuotes(prev => [...prev, quotes[currentLevel % quotes.length]]);
  };

  const nextLevel = () => {
    if (currentLevel < LEVELS.length - 1) {
      startLevel(currentLevel + 1);
    } else {
      setGameState('menu');
    }
  };

  const resetGame = () => {
    setGameState('menu');
    setCurrentLevel(0);
    setScore(0);
    setLives(3);
    setUnlockedBows(['Basic Bow']);
    setWisdomQuotes([]);
  };

  // Get target icon based on type
  const getTargetIcon = (type) => {
    switch (type) {
      case 'fish': return '🐟';
      case 'chakra': return '☸️';
      case 'orb': return '🔮';
      case 'demon': return '👹';
      case 'elemental': return '🌍'; // Will be customized per element
      default: return '🎯';
    }
  };

  // Get element icon
  const getElementIcon = (element) => {
    switch (element) {
      case 'earth': return '🌍';
      case 'water': return '💧';
      case 'fire': return '🔥';
      case 'air': return '💨';
      case 'space': return '🌌';
      default: return '⭐';
    }
  };

  return (
    <div className="flex flex-col items-center w-full">
      <div className="flex items-center justify-center mb-2">
        <img 
          src="/images/avatars/krishna.png" 
          alt="Arjuna" 
          className="w-10 h-10 md:w-12 md:h-12 object-contain mr-3 drop-shadow-md"
          onError={(e) => {
            e.target.src = '/images/avatars/rama.png';
          }}
        />
        <h1 className="text-3xl md:text-4xl font-bold text-blue-600 text-center">Arjuna's Archery Challenge</h1>
      </div>
      <p className="text-base md:text-lg text-gray-600 mb-4 md:mb-6 text-center px-4">
        Master the divine art of archery with Arjuna. Focus, aim, and shoot!
      </p>
      
      {/* Game stats */}
      <div className="flex flex-wrap justify-center gap-2 md:gap-4 w-full max-w-2xl mb-4 px-2">
        <div className="bg-blue-100 rounded-full px-3 py-1 md:px-4 md:py-2">
          <span className="font-bold text-blue-600 text-sm md:text-base">Score: {score}</span>
        </div>
        <div className="bg-red-100 rounded-full px-3 py-1 md:px-4 md:py-2">
          <span className="font-bold text-red-600 text-sm md:text-base">Lives: {lives}</span>
        </div>
        <div className="bg-blue-100 rounded-full px-3 py-1 md:px-4 md:py-2">
          <span className="font-bold text-blue-600 text-sm md:text-base">High Score: {highScore}</span>
        </div>
        <div className="bg-green-100 rounded-full px-3 py-1 md:px-4 md:py-2">
          <span className="font-bold text-green-600 text-sm md:text-base">Wind: {wind.speed.toFixed(1)} mph</span>
        </div>
      </div>
      
      {/* Game container */}
      <div 
        className="game-container relative bg-gradient-to-b from-sky-200 to-sky-400 border-2 md:border-4 border-blue-500 rounded-xl overflow-hidden shadow-xl w-full max-w-full"
        style={{ 
          width: gameDimensions.GAME_WIDTH, 
          height: gameDimensions.GAME_HEIGHT,
          maxWidth: '95vw'
        }}
        onMouseDown={startAiming}
      >
        {gameState === 'menu' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-50 z-10 p-2">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-gradient-to-br from-blue-100 to-sky-100 rounded-2xl p-4 md:p-8 text-center max-w-md w-full mx-2 border-2 md:border-4 border-blue-500 shadow-2xl"
            >
              <div className="flex justify-center mb-3 md:mb-5">
                <img 
                  src="/images/avatars/krishna.png" 
                  alt="Arjuna" 
                  className="w-24 h-24 md:w-32 md:h-32 object-contain drop-shadow-lg"
                  onError={(e) => {
                    e.target.src = '/images/avatars/rama.png';
                  }}
                />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-blue-600 mb-2 md:mb-4">Arjuna's Archery Challenge</h2>
              <p className="text-gray-700 text-sm md:text-base mb-2">
                Master the divine art of archery with Arjuna
              </p>
              <p className="text-gray-700 text-sm md:text-base mb-4 md:mb-6">
                Test your focus, patience, and precision in Guru Dronacharya's trials
              </p>
              <button
                onClick={startGame}
                className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 md:py-3 px-6 md:px-8 rounded-full text-lg md:text-xl transition-colors shadow-lg mb-4 md:mb-6 flex items-center justify-center mx-auto"
              >
                <span>Begin Training</span>
                <span className="ml-2">🏹</span>
              </button>
              <div className="bg-white rounded-xl p-3 md:p-4 mb-3 md:mb-4 shadow-md">
                <h3 className="font-bold text-blue-600 mb-2 text-sm md:text-base">Your Bows:</h3>
                <div className="flex flex-wrap justify-center gap-2">
                  {unlockedBows.map((bow, index) => (
                    <span key={index} className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs">
                      {bow}
                    </span>
                  ))}
                </div>
              </div>
              <div className="text-gray-500 text-xs md:text-sm">
                <p>Inspired by Arjuna's divine archery from Mahabharata</p>
              </div>
            </motion.div>
          </div>
        )}
        
        {gameState === 'levelSelect' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-50 z-10 p-2">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-gradient-to-br from-blue-100 to-sky-100 rounded-2xl p-4 md:p-8 text-center max-w-2xl w-full mx-2 border-2 md:border-4 border-blue-500 shadow-2xl max-h-[80vh] overflow-y-auto"
            >
              <h2 className="text-2xl md:text-3xl font-bold text-blue-600 mb-4">Guru Dronacharya's Trials</h2>
              <p className="text-gray-700 text-sm md:text-base mb-6">
                Complete each trial to master the divine art of archery
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {LEVELS.map((level, index) => (
                  <div 
                    key={level.id}
                    className={`bg-white rounded-xl p-4 shadow-md border-2 cursor-pointer transition-all hover:scale-105 ${
                      index <= currentLevel || unlockedBows.includes(level.reward.split(' ')[0]) 
                        ? 'border-blue-500' 
                        : 'border-gray-300 opacity-50'
                    }`}
                    onClick={() => (index <= currentLevel || unlockedBows.includes(level.reward.split(' ')[0])) && startLevel(index)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-bold text-blue-600 text-lg">Level {level.id}</h3>
                      <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                        {level.environment}
                      </span>
                    </div>
                    <h4 className="font-bold text-gray-800 mb-2">{level.name}</h4>
                    <p className="text-gray-600 text-sm mb-3">{level.description}</p>
                    <div className="flex justify-between items-center">
                      <span className="text-yellow-600 text-sm font-bold">{level.reward}</span>
                      {index <= currentLevel || unlockedBows.includes(level.reward.split(' ')[0]) ? (
                        <span className="text-green-500">✓ Unlocked</span>
                      ) : (
                        <span className="text-gray-400">Locked</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <button
                onClick={resetGame}
                className="mt-6 bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-6 rounded-full transition-colors"
              >
                Back to Menu
              </button>
            </motion.div>
          </div>
        )}
        
        {gameState === 'levelComplete' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-50 z-10 p-2">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-gradient-to-br from-yellow-100 to-orange-100 rounded-2xl p-6 md:p-8 text-center max-w-md w-full mx-2 border-2 md:border-4 border-yellow-500 shadow-2xl"
            >
              <h2 className="text-2xl md:text-3xl font-bold text-yellow-600 mb-2">Trial Completed!</h2>
              <div className="text-5xl mb-4">🏆</div>
              <h3 className="text-xl md:text-2xl font-bold text-blue-600 mb-4">
                {LEVELS[currentLevel].name}
              </h3>
              <p className="text-gray-700 mb-4">
                You have earned: <span className="font-bold text-yellow-600">{LEVELS[currentLevel].reward}</span>
              </p>
              
              {wisdomQuotes.length > 0 && (
                <div className="bg-white rounded-xl p-4 mb-6">
                  <h4 className="font-bold text-blue-600 mb-2">Wisdom of the Masters:</h4>
                  <p className="text-gray-700 italic text-sm">
                    "{wisdomQuotes[wisdomQuotes.length - 1]}"
                  </p>
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
                <button
                  onClick={nextLevel}
                  className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2 md:py-3 px-4 md:px-6 rounded-full transition-colors shadow-lg flex items-center justify-center text-sm md:text-base"
                >
                  {currentLevel < LEVELS.length - 1 ? 'Next Trial' : 'Main Menu'}
                </button>
                <button
                  onClick={() => startLevel(currentLevel)}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 md:py-3 px-4 md:px-6 rounded-full transition-colors shadow-lg flex items-center justify-center text-sm md:text-base"
                >
                  Retry Trial
                </button>
              </div>
            </motion.div>
          </div>
        )}
        
        {gameState === 'gameOver' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-50 z-10 p-2">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-2xl p-4 md:p-8 text-center max-w-md w-full mx-2"
            >
              <h2 className="text-2xl md:text-3xl font-bold text-red-600 mb-2">Training Failed!</h2>
              <div className="flex justify-center mb-3 md:mb-5">
                <img 
                  src="/images/avatars/krishna.png" 
                  alt="Arjuna" 
                  className="w-20 h-20 md:w-24 md:h-24 object-contain drop-shadow-lg"
                  onError={(e) => {
                    e.target.src = '/images/avatars/rama.png';
                  }}
                />
              </div>
              <p className="text-xl md:text-2xl text-gray-700 mb-2">Final Score: <span className="font-bold text-blue-600">{score}</span></p>
              {score === parseInt(highScore) && score > 0 && (
                <div className="mb-3 md:mb-4">
                  <p className="text-lg md:text-xl text-yellow-600 font-bold">🎉 New High Score! 🎉</p>
                  <p className="text-base md:text-lg text-yellow-500">You're a master archer!</p>
                  <div className="flex justify-center mt-1 md:mt-2">
                    {[...Array(5)].map((_, i) => (
                      <motion.div
                        key={i}
                        animate={{ 
                          y: [0, -15, 0],
                          rotate: [0, 360]
                        }}
                        transition={{ 
                          duration: 0.5,
                          delay: i * 0.1,
                          repeat: 3
                        }}
                        className="text-xl md:text-2xl mx-1"
                      >
                        🎊
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
              <div className="bg-blue-50 rounded-xl p-3 md:p-4 mb-4 md:mb-6">
                <h3 className="font-bold text-blue-600 mb-2 text-sm md:text-base">Guru Dronacharya's Advice:</h3>
                <p className="text-gray-700 italic text-xs md:text-sm">
                  "Failure is the foundation of success. Learn from this trial and try again with renewed focus."
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
                <button
                  onClick={() => startLevel(currentLevel)}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 md:py-3 px-4 md:px-6 rounded-full transition-colors shadow-lg flex items-center justify-center text-sm md:text-base"
                >
                  <span>Retry Trial</span>
                </button>
                <button
                  onClick={resetGame}
                  className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 md:py-3 px-4 md:px-6 rounded-full transition-colors shadow-lg flex items-center justify-center text-sm md:text-base"
                >
                  <span>Main Menu</span>
                </button>
              </div>
            </motion.div>
          </div>
        )}
        
        {/* Background elements based on level */}
        <div className={`absolute inset-0 bg-gradient-to-b ${LEVELS[currentLevel]?.background || 'from-sky-200 to-sky-400'}`}>
          {/* Environment-specific elements */}
          {LEVELS[currentLevel]?.environment === 'water' && (
            <>
              <div className="absolute bottom-0 w-full h-20 md:h-24 bg-gradient-to-t from-blue-700 to-blue-500"></div>
              <div className="absolute bottom-20 md:bottom-24 w-full h-4 bg-blue-300 opacity-50"></div>
            </>
          )}
          
          {LEVELS[currentLevel]?.environment === 'fire' && (
            <>
              {/* Fire sparks for Level 2 */}
              {fireSparks.map(spark => (
                <motion.div
                  key={spark.id}
                  className="absolute rounded-full bg-orange-500 opacity-70"
                  style={{
                    left: spark.x,
                    top: spark.y,
                    width: spark.size,
                    height: spark.size
                  }}
                  animate={{
                    y: [0, -20, 0],
                    opacity: [0.7, 1, 0.7],
                    scale: [1, 1.5, 1]
                  }}
                  transition={{
                    duration: 1,
                    repeat: Infinity
                  }}
                ></motion.div>
              ))}
              
              {/* Chakra ring visualization */}
              {currentLevel === 1 && targets.length > 0 && (
                <div 
                  className="absolute border-4 border-yellow-400 rounded-full opacity-30"
                  style={{
                    left: targets[0]?.centerX - targets[0]?.radius,
                    top: targets[0]?.centerY - targets[0]?.radius,
                    width: (targets[0]?.radius || 0) * 2,
                    height: (targets[0]?.radius || 0) * 2
                  }}
                ></div>
              )}
            </>
          )}
          
          {LEVELS[currentLevel]?.environment === 'storm' && (
            <>
              <div className="absolute top-0 left-0 w-full h-full bg-gray-800 opacity-30"></div>
              <motion.div
                className="absolute top-10 left-1/4 w-12 h-12 md:w-16 md:h-16 bg-yellow-300 rounded-full opacity-80"
                animate={{
                  opacity: [0.8, 0.2, 0.8]
                }}
                transition={{
                  duration: 0.5,
                  repeat: Infinity
                }}
              ></motion.div>
            </>
          )}
        </div>
        
        {/* Bow (always visible when playing) */}
        {gameState === 'playing' && (
          <div 
            className="absolute w-16 h-16 flex items-center justify-center"
            style={{
              left: arrowPosition.x - 30,
              top: arrowPosition.y - 30
            }}
          >
            <div className="text-4xl">🏹</div>
          </div>
        )}
        
        {/* Arrow */}
        {gameState === 'playing' && (arrowVelocity.x !== 0 || arrowVelocity.y !== 0) && (
          <motion.div
            className="absolute"
            style={{
              left: arrowPosition.x,
              top: arrowPosition.y,
              width: gameDimensions.ARROW_WIDTH,
              height: gameDimensions.ARROW_HEIGHT
            }}
            animate={{ 
              rotate: Math.atan2(arrowVelocity.y, arrowVelocity.x) * 180 / Math.PI
            }}
          >
            <div className="w-full h-full flex items-center">
              <div className="text-2xl">⇨</div>
            </div>
          </motion.div>
        )}
        
        {/* Aiming line */}
        {gameState === 'playing' && isAiming && (
          <svg className="absolute top-0 left-0 w-full h-full pointer-events-none">
            <line
              x1={arrowPosition.x}
              y1={arrowPosition.y}
              x2={arrowPosition.x + Math.cos(aimAngle) * aimPower * 10}
              y2={arrowPosition.y + Math.sin(aimAngle) * aimPower * 10}
              stroke="white"
              strokeWidth="2"
              strokeDasharray="5,5"
            />
          </svg>
        )}
        
        {/* Targets */}
        {gameState === 'playing' && targets.map(target => {
          // Adjust target size based on type
          const targetSize = target.size || gameDimensions.TARGET_SIZE;
          return (
            <motion.div
              key={target.id}
              className="absolute flex items-center justify-center"
              style={{
                left: target.x - targetSize / 2,
                top: target.y - targetSize / 2,
                width: targetSize,
                height: targetSize
              }}
              animate={{ 
                scale: [1, 1.1, 1]
              }}
              transition={{ 
                scale: { duration: 1, repeat: Infinity }
              }}
            >
              <div className="w-full h-full flex items-center justify-center text-2xl md:text-3xl bg-white bg-opacity-20 rounded-full border-2 border-white">
                {target.element ? getElementIcon(target.element) : getTargetIcon(target.type)}
              </div>
            </motion.div>
          );
        })}
        
        {/* Wind indicator */}
        {gameState === 'playing' && wind.speed > 0 && (
          <div className="absolute top-4 right-4 flex items-center bg-white bg-opacity-50 rounded-full px-3 py-1">
            <span className="mr-2">💨</span>
            <span>{wind.speed.toFixed(1)} mph</span>
            <motion.div
              className="ml-2 w-6 h-2 bg-blue-500 rounded"
              animate={{
                x: [0, wind.direction * 10, 0]
              }}
              transition={{
                duration: 1,
                repeat: Infinity
              }}
            ></motion.div>
          </div>
        )}
        
        {/* Level info */}
        {gameState === 'playing' && (
          <div className="absolute top-4 left-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full">
            <span className="font-bold">Level {currentLevel + 1}:</span> {LEVELS[currentLevel]?.name}
          </div>
        )}
      </div>
      
      {/* Game instructions */}
      <div className="mt-4 md:mt-6 text-center max-w-2xl px-2">
        <div className="bg-blue-100 rounded-xl p-3 md:p-4 mb-3 md:mb-4">
          <p className="text-gray-700 mb-1 md:mb-2 text-sm md:text-base">
            <span className="font-bold text-blue-600">Controls:</span> Click and drag to aim, release to shoot
          </p>
          <p className="text-gray-600 text-xs md:text-sm">
            Adjust for wind and hit the targets to advance through Guru Dronacharya's trials
          </p>
        </div>
        {gameState === 'playing' && (
          <div className="bg-yellow-100 rounded-xl p-3 md:p-4 mb-3 md:mb-4">
            <p className="text-gray-700 text-sm md:text-base">
              <span className="font-bold text-yellow-600">Level {currentLevel + 1} - {LEVELS[currentLevel]?.name}:</span> {LEVELS[currentLevel]?.description}
            </p>
          </div>
        )}
      </div>
      
      {/* Mobile controls */}
      <div className="md:hidden absolute bottom-4 left-0 right-0 flex justify-center">
        <button
          className="bg-blue-500 text-white px-6 py-3 rounded-full shadow-lg text-lg"
          onTouchStart={startAiming}
        >
          Aim & Shoot
        </button>
      </div>

      {/* Story section */}
      <div className="mt-6 md:mt-8 bg-blue-50 rounded-2xl p-4 md:p-6 max-w-2xl w-full mx-2">
        <h3 className="text-xl md:text-2xl font-bold text-blue-600 mb-2 md:mb-3">The Training of Arjuna</h3>
        <p className="text-gray-700 mb-2 md:mb-3 text-sm md:text-base">
          Under the guidance of Guru Dronacharya, Arjuna mastered the divine art of archery through intense focus and discipline.
        </p>
        <p className="text-gray-700 text-sm md:text-base">
          Each trial in this game represents a lesson Arjuna learned on his path to becoming the greatest archer of his time.
        </p>
      </div>
    </div>
  );
};

export default ArjunaArcheryChallenge;